import { useState, useRef, useEffect } from 'react';
import { ArrowDownTrayIcon, Cog6ToothIcon, StarIcon } from '@heroicons/react/24/outline';
import type { ExportViewState } from '@/api/dataVault';
import { apiClient } from '@/api/client';

interface PresetItem {
  id: string;
  name: string;
  filters_json: any;
}

interface Props {
  viewState: ExportViewState;
  csvUrl: string;
  xlsxUrl?: string;
  geojsonUrl?: string;
  bulkZipUrl?: string;
  presets?: PresetItem[];
  onLoadPreset?: (preset: PresetItem) => void;
  onExportChart?: () => void;
  onCopyChart?: () => void;
  onCustomExport?: () => void;
}

const TAB_LABELS: Record<string, string> = {
  analytical: 'ANALYTICAL',
  gauging: 'WATER LEVELS',
  risk: 'RISK',
  cost: 'COST',
  plume: 'PLUME',
  compliance: 'COMPLIANCE',
};

const CHART_LABELS: Record<string, string> = {
  timeseries: 'TIMESERIES',
  bar: 'BAR CHART',
  heatmap: 'HEATMAP',
  scatter: 'SCATTER',
  distribution: 'DISTRIBUTION',
  regression: 'TREND',
};

export function ExportMenu({
  viewState, csvUrl, xlsxUrl, geojsonUrl, bulkZipUrl,
  presets, onLoadPreset, onExportChart, onCopyChart, onCustomExport,
}: Props) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  // "Include QC + raw data" toggle: when on, append include_qc=true so the
  // analytical CSV / Excel(all tabs) / bulk ZIP include QC riders + a Raw
  // Record (JSON) column. Off by default — general exports are field-only.
  const [includeQcRaw, setIncludeQcRaw] = useState(false);
  const withQc = (u: string) =>
    includeQcRaw ? `${u}${u.includes('?') ? '&' : '?'}include_qc=true` : u;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const downloadFile = async (url: string, format: string) => {
    setDownloading(format);
    try {
      // Use apiClient (not raw fetch) so the response interceptor handles
      // 401 -> token refresh -> retry. Raw fetch bypassed it, so an expired
      // JWT during export surfaced as a "security" error / forced logout.
      const resp = await apiClient.get(url, { responseType: 'blob' });
      const blob = resp.data as Blob;
      const disposition = (resp.headers['content-disposition'] as string) || '';
      const match = disposition.match(/filename=(.+)/);
      const extMap: Record<string, string> = { xlsx: 'xlsx', csv: 'csv', geojson: 'geojson', zip: 'zip', pdf: 'pdf' };
      const filename = match ? match[1] : `argos_export.${extMap[format] || 'bin'}`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } finally {
      setDownloading(null);
    }
    setOpen(false);
  };

  const tabLabel = TAB_LABELS[viewState.activeTab] || viewState.activeTab.toUpperCase();
  const chartLabel = CHART_LABELS[viewState.chartType || 'timeseries'] || 'CHART';
  const isChartView = viewState.viewMode === 'chart';
  const isPlume = viewState.activeTab === 'plume';
  const hasCsv = viewState.activeTab === 'analytical' || viewState.activeTab === 'gauging';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-mono-800 border border-mono-700 text-mono-200 text-[11px] tracking-wider rounded hover:border-mono-500 transition-colors"
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        EXPORT
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 right-0 w-56 bg-mono-900 border border-mono-700 rounded shadow-xl">

          {/* ── Include QC + raw data toggle (analytical CSV / Excel-all / bulk) ── */}
          <button
            type="button"
            onClick={() => setIncludeQcRaw((v) => !v)}
            className="w-full px-3 py-2 flex items-center gap-2 text-left text-[11px] tracking-wider text-mono-300 hover:bg-mono-800 hover:text-mono-100 transition-colors"
          >
            <span
              aria-hidden
              className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                includeQcRaw ? 'bg-cyan-500 border-cyan-500' : 'bg-mono-800 border-mono-600'
              }`}
            >
              {includeQcRaw && (
                <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-mono-950" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="2,6.5 5,9 10,3.5" />
                </svg>
              )}
            </span>
            INCLUDE QC + RAW DATA
          </button>
          <div className="border-t border-mono-800 my-1" />

          {/* ── Primary actions (context-aware) ── */}
          {isChartView ? (
            <>
              {onExportChart && (
                <ExportButton
                  label={`PNG (${chartLabel})`}
                  loading={downloading === 'chart-png'}
                  loadingLabel="EXPORTING..."
                  onClick={() => { onExportChart(); setOpen(false); }}
                />
              )}
              {onCopyChart && (
                <ExportButton
                  label="COPY CHART TO CLIPBOARD"
                  loading={false}
                  loadingLabel=""
                  onClick={() => { onCopyChart(); setOpen(false); }}
                />
              )}
            </>
          ) : (
            <>
              {hasCsv && (
                <ExportButton
                  label={`CSV (${tabLabel})`}
                  loading={downloading === 'csv'}
                  loadingLabel="EXPORTING..."
                  onClick={() => downloadFile(withQc(csvUrl), 'csv')}
                />
              )}
              {isPlume && geojsonUrl && (
                <ExportButton
                  label="GEOJSON (PLUME + WELLS)"
                  loading={downloading === 'geojson'}
                  loadingLabel="EXPORTING..."
                  onClick={() => downloadFile(geojsonUrl, 'geojson')}
                />
              )}
              {!hasCsv && !isPlume && (
                <ExportButton
                  label={`EXCEL (${tabLabel})`}
                  loading={downloading === 'xlsx-tab'}
                  loadingLabel="EXPORTING..."
                  onClick={() => xlsxUrl && downloadFile(xlsxUrl, 'xlsx-tab')}
                />
              )}
            </>
          )}

          {/* ── Divider ── */}
          <div className="border-t border-mono-800 my-1" />

          {/* ── Multi-tab / bulk options ── */}
          {xlsxUrl && (
            <ExportButton
              label="EXCEL (ALL TABS)"
              loading={downloading === 'xlsx'}
              loadingLabel="EXPORTING..."
              onClick={() => downloadFile(withQc(xlsxUrl), 'xlsx')}
            />
          )}
          {bulkZipUrl && (
            <ExportButton
              label="BULK ZIP (ALL DATA)"
              loading={downloading === 'zip'}
              loadingLabel="BUILDING ZIP..."
              onClick={() => downloadFile(withQc(bulkZipUrl), 'zip')}
            />
          )}

          {/* ── Custom Export ── */}
          <div className="border-t border-mono-800 my-1" />
          <button
            disabled={!onCustomExport}
            onClick={() => { onCustomExport?.(); setOpen(false); }}
            className={`w-full px-3 py-2 text-left text-[11px] tracking-wider flex items-center gap-1.5 transition-colors ${
              onCustomExport
                ? 'text-mono-300 hover:bg-mono-800 hover:text-mono-100 cursor-pointer'
                : 'text-mono-600 cursor-not-allowed'
            }`}
          >
            <Cog6ToothIcon className="h-3 w-3" />
            CUSTOM EXPORT...
          </button>

          {/* ── Presets ── */}
          {presets && presets.length > 0 && onLoadPreset && (
            <>
              <div className="border-t border-mono-800 my-1" />
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => { onLoadPreset(preset); setOpen(false); }}
                  className="w-full px-3 py-2 text-left text-[11px] text-mono-300 hover:bg-mono-800 hover:text-mono-100 tracking-wider transition-colors flex items-center gap-1.5"
                >
                  <StarIcon className="h-3 w-3 text-amber-500/60" />
                  {preset.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ExportButton({ label, loading, loadingLabel, onClick }: {
  label: string;
  loading: boolean;
  loadingLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full px-3 py-2 text-left text-[11px] text-mono-300 hover:bg-mono-800 hover:text-mono-100 tracking-wider transition-colors disabled:opacity-50"
    >
      {loading ? loadingLabel : label}
    </button>
  );
}
